import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Confetti } from "@/components/redemption/experiential/Confetti";

/** World-map backdrop behind the copy (prototype `.success-page` background-image). */
const WORLD_MAP_BACKDROP =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/World_Map_Whiteout2.png?v=1777650043";

/**
 * Manual-send "preparing your gift links" beat. The links already exist the moment launch
 * succeeds (minted in `launchWithin`), so this is a soft, near-real-time pause — just long enough
 * to read the celebration before the download CTA arrives — not a wait for any real work. The
 * prototype's original `SUCCESS_PREP_MS` was 10s; shortened here to keep it snappy.
 */
const PREP_MS = 1500;

/**
 * The spinner's fade-out completes before the CTA fades in, so the two motions
 * don't compete for the same slot (prototype `showSuccessPage`, ~line 24250).
 */
const SPINNER_FADE_MS = 420;

/** The title, grouped into the two rendered lines. */
const TITLE_LINES = [
  ["Thank", "you", "for"],
  ["doing", "good", "with", "us!"],
];

/**
 * The heading's accessible name. The words are separate boxes so each can animate
 * on its own, which leaves no whitespace between them for assistive tech to read —
 * so the name is supplied here instead and the word spans are hidden from AT.
 */
const TITLE_TEXT = "Thank you for doing good with us!";

/**
 * Each word paired with its entrance order, so the stagger keeps running across
 * the line break instead of restarting on the second line.
 */
const TITLE_WORDS = TITLE_LINES.map((line, lineIndex) => {
  const offset = TITLE_LINES.slice(0, lineIndex).reduce((count, l) => count + l.length, 0);
  return {
    key: line.join("-"),
    words: line.map((word, wordIndex) => ({ word, order: offset + wordIndex })),
  };
});

const WORD_STAGGER_MS = 90;
const SUBTITLE_DELAY_MS = 1050;
const CTA_DELAY_MS = 1300;

/**
 * Transition durations, one literal class each. Tailwind scans source text, so a
 * class assembled at runtime (`duration-[${ms}ms]`) would never be generated.
 */
const DURATION = {
  overlay: "duration-[450ms]",
  word: "duration-[600ms]",
  subtitle: "duration-[550ms]",
  spinner: "duration-[400ms]",
  cta: "duration-[550ms]",
} as const;

/** Must clear the overlay root's `z-10000`; Confetti's own default (7010) stacks behind it. */
const CONFETTI_Z_INDEX = 10010;

export interface LaunchSuccessOverlayProps {
  /** Controlled by the checkout column — this overlay is not in the modal registry. */
  open: boolean;
  /**
   * Manual-send campaign: the sender distributes the gift links themselves, so the
   * overlay waits out a preparation beat and then offers the download instead of
   * pointing at the dashboard.
   */
  isManual: boolean;
  onGoToDashboard: () => void;
  /**
   * Only reachable in manual mode, once the prepared links are offered. Optional
   * because the app has no gift-link export yet: when it is absent the manual CTA
   * falls back to the dashboard rather than advertising a download that does
   * nothing — this overlay is terminal, so a no-op CTA strands the user.
   */
  onDownloadLinks?: () => void;
  /**
   * Universal-link campaign: the ONE public URL to share. When present, the CTA is "Copy Universal
   * Link" (no preparation beat — the link already exists), and once copied a "Visit Dashboard" link
   * appears below it. Takes precedence over the manual/email CTAs.
   */
  universalLink?: string;
}

/**
 * The post-launch celebration. Mirrors the prototype's `#successPage`
 * (Campaign-Builder-Master-Prototype.html, markup ~line 10905, JS
 * `showSuccessPage()` ~line 24199).
 *
 * A terminal state by design: there is no dismiss affordance and no Escape
 * handler, so the CTA is the only way out and must always do something — see
 * `onDownloadLinks`. The entrance is word-by-word and is skipped wholesale under
 * `prefers-reduced-motion`; the manual-mode preparation wait is not, since that
 * delay represents work rather than decoration.
 *
 * Opening also fires the shared `Confetti` burst (GFG-240), which suppresses
 * itself — visuals and audio together — under `prefers-reduced-motion`.
 */
export default function LaunchSuccessOverlay({
  open,
  isManual,
  onGoToDashboard,
  onDownloadLinks,
  universalLink,
}: LaunchSuccessOverlayProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const isUniversal = universalLink !== undefined;

  const [entered, setEntered] = useState(false);
  const [spinnerFading, setSpinnerFading] = useState(false);
  const [ctaReady, setCtaReady] = useState(false);
  const [copied, setCopied] = useState(false);
  // Copy can fail (permission denied, non-secure origin, no Clipboard API). This overlay is terminal
  // — "Visit Dashboard" only appears after a copy — so on failure we must still reveal the exit, or
  // the user is stranded until a reload.
  const [copyFailed, setCopyFailed] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [prefersReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  // Replay the entrance on every open — a second launch in the same session must
  // not inherit the previous pass's finished state.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      setSpinnerFading(false);
      setCtaReady(false);
      setCopied(false);
      setCopyFailed(false);
      return;
    }
    if (prefersReduced) {
      setEntered(true);
      return;
    }
    // Two frames: the first commits the "before" styles, the second flips the
    // transitions on (the prototype's two-step fade convention).
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [open, prefersReduced]);

  // Manual mode: the spinner runs for the preparation window, fades, and only then
  // does the download CTA arrive. Both timers are cleared on close so a re-launch
  // can't fire a stale callback against fresh state.
  useEffect(() => {
    if (!open || !isManual) return;
    let fadeTimer = 0;
    const prepTimer = window.setTimeout(() => {
      setSpinnerFading(true);
      fadeTimer = window.setTimeout(() => setCtaReady(true), SPINNER_FADE_MS);
    }, PREP_MS);
    return () => {
      window.clearTimeout(prepTimer);
      window.clearTimeout(fadeTimer);
    };
  }, [open, isManual]);

  // Keyed on `open`, not on mount: this component stays mounted while closed, so a second
  // launch in the same session must re-fire the burst the way `entered` replays the entrance.
  // Not gated on `ctaReady` — the campaign is already live, and waiting would leave manual mode's
  // success screen bare for the whole PREP_MS beat, which is the complaint this answers.
  useEffect(() => {
    if (!open) return;
    setConfettiTrigger((t) => t + 1);
  }, [open]);

  // The overlay owns the viewport while it is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Email mode hands focus straight to the CTA. Manual mode can't — the CTA is
  // inert for the preparation window — so focus parks on the dialog and moves to
  // the CTA the moment it becomes actionable.
  useEffect(() => {
    if (!open) return;
    if (isManual && !ctaReady) {
      panelRef.current?.focus();
      return;
    }
    ctaRef.current?.focus();
  }, [open, isManual, ctaReady]);

  if (!open) return null;

  const subtitle = isUniversal
    ? "Copy your universal link and share it anywhere — anyone with it can redeem a gift."
    : isManual
      ? "Please hang tight while we prepare your list of gift links."
      : "Visit your dashboard to track the progress of your gift delivery.";

  // Manual mode defers the CTA until the links are ready; email + universal reveal it on a delay but
  // keep it reachable from the start, so focus has somewhere to land.
  const ctaShown = isManual ? ctaReady : entered;
  const ctaInert = isManual && !ctaReady;
  const showSpinner = isManual && !ctaReady;

  // The manual flow only offers the download when there is one to offer. Without an
  // export the CTA becomes the dashboard link, since this overlay has no other exit.
  const offersDownload = isManual && onDownloadLinks !== undefined;

  // Universal: the CTA copies the public link (the overlay stays open), and a "Visit Dashboard" link
  // appears below once it's been copied — the overlay's exit.
  const copyUniversalLink = () => {
    if (!universalLink) return;
    const done = navigator.clipboard?.writeText(universalLink);
    // No Clipboard API at all (older browser / insecure origin): writeText is absent, so reveal the
    // exit immediately rather than leaving a button that silently does nothing.
    if (!done) {
      setCopyFailed(true);
      return;
    }
    done.then(() => setCopied(true)).catch(() => setCopyFailed(true));
  };

  // Reduced motion drops both halves of the entrance: no stagger, no transition.
  const motion = (delayMs: number) =>
    prefersReduced ? undefined : { transitionDelay: `${delayMs}ms` };
  const duration = (key: keyof typeof DURATION) => (prefersReduced ? "duration-0" : DURATION[key]);

  return createPortal(
    <>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(244,239,234,0.78) 100%), url("${WORLD_MAP_BACKDROP}")`,
        }}
        className={`fixed inset-0 z-10000 flex items-center justify-center overflow-hidden bg-clear-dust bg-center bg-cover bg-no-repeat outline-none transition-opacity ease-out ${duration("overlay")} ${entered ? "opacity-100" : "opacity-0"}`}
      >
        <div className="relative max-w-195 px-6 text-center">
          <h1
            id={titleId}
            aria-label={TITLE_TEXT}
            className="mb-5 font-crimson font-normal text-[64px] text-rodeo-black leading-[1.1] tracking-[-0.5px] max-[720px]:text-[44px]"
          >
            {TITLE_WORDS.map((line) => (
              <span key={line.key} aria-hidden="true" className="block">
                {line.words.map(({ word, order }) => (
                  <span
                    key={word}
                    style={motion(order * WORD_STAGGER_MS)}
                    className={`mr-[0.22em] inline-block transition-[opacity,transform] ease-out last:mr-0 ${duration("word")} ${
                      entered
                        ? "translate-y-0 scale-100 opacity-100"
                        : "translate-y-7 scale-90 opacity-0"
                    }`}
                  >
                    {word}
                  </span>
                ))}
              </span>
            ))}
          </h1>

          <p
            style={motion(SUBTITLE_DELAY_MS)}
            className={`mb-8.5 font-lato text-[17px] text-graphite transition-[opacity,transform] ease-out ${duration("subtitle")} ${
              entered ? "translate-y-0 opacity-100" : "translate-y-2.5 opacity-0"
            } max-[720px]:mb-7 max-[720px]:text-[16px]`}
          >
            {subtitle}
          </p>

          {/* Fixed height so the spinner → CTA handover doesn't shift the layout. */}
          <div className="relative flex min-h-14 items-center justify-center max-[720px]:min-h-12">
            {showSpinner && (
              <div
                role="status"
                aria-label="Preparing gift links"
                style={motion(spinnerFading ? 0 : 550)}
                className={`absolute h-12 w-12 animate-spin rounded-full border-4 border-light-dust border-t-dark-dust transition-opacity ease-out ${duration("spinner")} ${
                  entered && !spinnerFading ? "opacity-100" : "opacity-0"
                } max-[720px]:h-10.5 max-[720px]:w-10.5 max-[720px]:border-[3px]`}
              />
            )}

            <button
              ref={ctaRef}
              type="button"
              tabIndex={ctaInert ? -1 : undefined}
              aria-hidden={ctaInert || undefined}
              onClick={
                isUniversal ? copyUniversalLink : offersDownload ? onDownloadLinks : onGoToDashboard
              }
              style={motion(isManual ? 0 : CTA_DELAY_MS)}
              className={`inline-flex cursor-pointer items-center gap-2 rounded-btn bg-rodeo-black px-7 py-3.5 font-bold font-lato text-[13px] text-white uppercase tracking-[0.7px] transition-[opacity,transform,background-color,box-shadow] ease-out hover:bg-[#222] hover:shadow-[0_6px_14px_rgba(0,0,0,0.12)] ${duration("cta")} ${
                ctaShown
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none translate-y-2.5 opacity-0"
              } max-[720px]:px-5.5 max-[720px]:py-3`}
            >
              {isUniversal
                ? copied
                  ? "Link copied"
                  : "Copy Universal Link"
                : offersDownload
                  ? "Download Gift Links"
                  : "Go to Dashboard"}
              {isUniversal ? (
                <ContentCopyIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              ) : offersDownload ? (
                <FileDownloadOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              ) : (
                <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              )}
            </button>
          </div>

          {/* Universal: the dashboard exit appears once the link has been copied — OR once a copy
              attempt failed, so a clipboard error can never strand the user on this terminal screen
              (they can copy the link from the campaign's menu on the dashboard). */}
          {isUniversal && (copied || copyFailed) && (
            <button
              type="button"
              onClick={onGoToDashboard}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent font-lato font-semibold text-[14px] text-dust-ink underline underline-offset-2 transition-colors hover:text-rodeo-black"
            >
              Visit Dashboard
              <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 15 }} />
            </button>
          )}
        </div>
      </div>

      <Confetti trigger={confettiTrigger} zIndex={CONFETTI_Z_INDEX} />
    </>,
    document.body,
  );
}
